<template>
    <div class="py-16 xl:py-36 px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
        <div class="max-w-max lg:max-w-7xl mx-auto">
            <div class="relative z-10 mb-8 md:mb-2 md:px-6">
                <div class="text-base max-w-prose lg:max-w-none">
                    <h2 class="leading-6 text-gray-900 font-semibold tracking-wide uppercase">Feature Level Permission Testing</h2>
                    <div    class="w-1/4">
                        <select2    :options="sites"
                                    v-model:value="site"
                                    placeholder="Select A Site"
                                    :close-on-select="true"
                        ></select2>
                    </div>
                </div>
            </div>

            <!-- Checks for hiding content based on permission -->
            <div class="relative md:bg-white md:p-6">
                <div class="lg:grid lg:grid-cols-3 lg:gap-6">
                    <div class="prose prose-indigo prose-lg text-gray-500 lg:max-w-none">
                        <p      class="font-semibold text-lg">Column 1 - Feature Level Testing</p>
                        <p>
                            <span    class="font-semibold">Feature Level:</span>
                            {{ featureLevel.getFeatureLevelName(site) }}
                        </p>

                        <ul>
                            <li     v-if="featureLevel.hasFeature(featureExcursionReport, site)">
                                Can Use Feature Level Good (report_excursion)
                            </li>

                            <li     v-if="featureLevel.hasFeature(featureMonthlyDataLogs, site)">
                                Can Use Feature Level Better (report_monthly_data_logs)
                            </li>

                            <li     v-if="featureLevel.hasFeature(featureViewAllMultiPoint, site)">
                                Can Use Feature Level Best (point_view_multi_all)
                            </li>
                        </ul>
                    </div>

                    <!-- Column 2 -->
                    <div class="mt-6 prose prose-indigo prose-lg text-gray-500 lg:mt-0">
                        <p      class="font-semibold text-lg">Column 2 - Permission Level Testing</p>
                        <p>
                            <span    class="font-semibold">Permission Level:</span>
                            {{ permissions.getPermissionLevelName(site) }}
                        </p>

                        <ul>
                            <li     v-if="permissions.can('edit_view_permission', site)">
                                View No-Alarm User (edit_view_permission)
                            </li>

                            <li     v-if="permissions.can('view_meter', site)">
                                View User (view_meter)
                            </li>

                            <li     v-if="permissions.can('edit_admin_permission', site)">
                                Admin User (edit_admin_permission)
                            </li>

                            <li     v-if="permissions.can('edit_super_admin_permission', site)">
                                Super Admin User (edit_super_admin_permission)
                            </li>

                            <li     v-if="permissions.can('edit_sonicu_view_permission', site)">
                                Sonicu View Only (edit_sonicu_view_permission)
                            </li>

                            <li     v-if="permissions.can('do_anything', site)">
                                Sonicu (do_anything)
                            </li>

                            <li     v-if="permissions.isSonicu(site)">
                                Is Sonicu User
                            </li>

                            <li     v-if="permissions.isSonicuViewOnly(site)">
                                Is Sonicu View-Only User
                            </li>
                        </ul>
                    </div>

                    <!-- Column 3 - Environment Testing -->
                    <div class="mt-6 prose prose-indigo prose-lg text-gray-500 lg:mt-0">
                        <p      class="font-semibold text-lg">Column 3 - Environment Testing</p>

                        <ul>
                            <li     v-if="environment.isLocal()">
                                Local
                            </li>

                            <li     v-if="environment.isStaging()">
                                Staging
                            </li>

                            <li     v-if="environment.isQa()">
                                Qa
                            </li>

                            <li     v-if="environment.isProduction()">
                                Production
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <!-- Feature Level & Permission Level Listing. -->
        <div class="relative">
            <div class="relative md:bg-white md:p-6">
                <div class="lg:grid lg:grid-cols-2 lg:gap-6">
                    <div class="prose prose-indigo prose-lg text-gray-500 lg:max-w-none">
                        <p      class="font-semibold text-lg">Column 1 - Feature Level Testing</p>
                        <p>There are {{ featureLevel.getFeatures(site).length }} features in this feature level.</p>

                        <ul>
                            <li     v-for="feature in featuresArray()">
                                {{ feature }}
                            </li>
                        </ul>
                    </div>

                    <!-- Column 2 -->
                    <div class="mt-6 prose prose-indigo prose-lg text-gray-500 lg:mt-0">
                        <p      class="font-semibold text-lg">Column 2 - Permission Level Testing</p>
                        <p>There are {{ permissions.getPermissions(site).length }} permissions.</p>

                        <ul>
                            <li     v-for="permission in permissionsArray()">
                                {{ permission }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import axios from "axios"
import {companyMixin} from '@/mixins/companyMixin'
import {permissionMixin} from '@/mixins/permissionMixin'
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {Features} from "@/mixins/AuthorizationHelpers/Features";
import FloatingInput from "@/components/elements/FloatingInput";
import Select2 from "@/components/elements/select2";

export default {
    name: "FeatureLevelPermission",
    mixins: [companyMixin, permissionMixin, iconNamesMixin, SonicuCoreMixin],
    components: {Select2, FloatingInput},
    props: {
        sites: Object,
    },
    data() {
        return {
            site: '',
        }
    },
    created() {
        this.site = this.company.getCompanyId()
    },
    mounted() {
    },
    methods: {
        featuresArray() {
            return this.featureLevel.getFeatures(this.site)
        },
        permissionsArray() {
            return this.permissions.getPermissions(this.site)
        },
    },
    computed: {
        featureExcursionReport() {
            return Features.REPORT_EXCURSION
        },
        featureMonthlyDataLogs() {
            return Features.REPORT_MONTHLY_DATA_LOGS
        },
        featureViewAllMultiPoint() {
            return Features.POINT_VIEW_ALL_MULTI
        },
    },
    watch: {
        site() {
            console.log(`Site: ${this.site}`)
            this.$forceUpdate()
        }
    },
}
</script>

<style scoped>

</style>